import { NgFor, AsyncPipe } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  OnInit,
  TemplateRef,
  ViewChild,
  inject,
} from '@angular/core';
import { FormArray, FormControl, Validators } from '@angular/forms';
import { MatButton } from '@angular/material/button';
import { MatSelectChange, MatSelect, MatOption } from '@angular/material/select';
import { MatTableDataSource } from '@angular/material/table';
import { CoreSelectors } from '@next/core-lib';
import { RootState } from '@next/core-lib/app';
import { LocaleService, TranslatePipe } from '@next/core-lib/i18n';
import { TableColumnTypes, TableConfig, TableComponent } from '@next/core-lib/table';
import { Store } from '@ngrx/store';
import { Observable } from 'rxjs';
import { take } from 'rxjs/operators';
import { Barn } from 'src/app/cycle/models/barn-service.model';

import { SensorActuatorBarnMapping } from '../../models/farm-result.model';
import * as settingActions from '../../store/actions/setting.actions';
import { getBarns, getFarmResultSettings } from '../../store/selectors/setting.selector';

@Component({
  selector: 'app-farm-result-setting',
  templateUrl: './farm-result-setting.component.html',
  styleUrls: [],
  imports: [TableComponent, MatSelect, MatOption, NgFor, MatButton, AsyncPipe, TranslatePipe],
})
export class FarmResultSettingComponent implements OnInit, AfterViewInit {
  private readonly store = inject<Store<RootState>>(Store);
  private changeDetectorRef = inject(ChangeDetectorRef);
  private readonly locale = inject<LocaleService>(LocaleService);

  barnControls = new FormArray([]);
  sensorActuatorControls = new FormArray([]);

  farmResultSettings$ = this.store.select(getFarmResultSettings);
  barns$: Observable<Barn[]> = this.store.select(getBarns);
  isLoading$: Observable<boolean> = this.store.select(CoreSelectors.selectLoading);

  sensorActuatorsLength: number;

  /* TEMPLATES */
  @ViewChild('barnNameTemplate') barnNameTemplate: TemplateRef<Barn>;

  /* TABLE CONFIG */
  tableConfig: TableConfig = {
    dataSource: new MatTableDataSource([] as SensorActuatorBarnMapping[]),
    selectColumn: false,
    pagination: false,
    columns: [],
  };

  /** Inserted by Angular inject() migration for backwards compatibility */
  constructor(...args: unknown[]);

  constructor() {}

  ngOnInit(): void {
    this.initTableConfig();
  }

  ngAfterViewInit() {
    // Set barnName column template.
    const newConfig = { ...this.tableConfig };
    const columnBarnName = newConfig.columns.find((c) => c.id === 'barnId');

    columnBarnName.template = this.barnNameTemplate;
    this.tableConfig = newConfig;
    this.changeDetectorRef.detectChanges();
  }

  initTableConfig() {
    this.tableConfig = {
      dataSource: new MatTableDataSource([] as SensorActuatorBarnMapping[]),
      selectColumn: false,
      pagination: false,
      columns: [
        {
          id: 'sensorActuatorId',
          type: TableColumnTypes.STRING,
          inputConfig: {
            formArray: this.sensorActuatorControls,
            options: [Validators.required],
          },
          label: this.locale.translate('farm-result.table-header.sensorActuator'),
        },
        {
          id: 'sensorActuatorName',
          type: TableColumnTypes.STRING,
          label: this.locale.translate('farm-result.table-header.sensorActuatorName'),
        },
        {
          id: 'farmResultLocationId',
          type: TableColumnTypes.STRING,
          label: this.locale.translate('farm-result.table-header.farmResultLocationId'),
        },
        {
          id: 'barnId',
          type: TableColumnTypes.TEMPLATE,
          label: this.locale.translate('farm-result.table-header.barn'),
          inputConfig: {
            formArray: this.barnControls,
            options: [Validators.required],
          },
        },
      ],
      scrolling: true,
      width: '1200px',
      wrapperWidth: '100%',
      enableArrowKeys: true,
    };

    this.farmResultSettings$.subscribe((farmResultSettings) => {
      this.tableConfig.dataSource.data = farmResultSettings;
      this.sensorActuatorsLength = farmResultSettings.length;
    });
  }

  getBarn(row: SensorActuatorBarnMapping): Barn {
    let foundBarn = <Barn>{};
    if (row.barnId) {
      this.barns$.pipe(take(1)).subscribe((barns) => {
        foundBarn = !!barns && barns.find((barn) => barn.id === row.barnId);
      });
    }
    const barnControl = new FormControl();
    barnControl.setValue(foundBarn);

    const sensorActuatorBarnRows = this.tableConfig.dataSource.data.map(
      (p: SensorActuatorBarnMapping) => Object.assign({}, p),
    );

    const bcIndex = this.barnControls.controls.findIndex((bc) => bc.value?.id === row.barnId);

    if (this.sensorActuatorsLength !== this.barnControls.length) {
      if (
        bcIndex === -1 ||
        (bcIndex > -1 &&
          sensorActuatorBarnRows.find((sab) => sab.id === row.id).sensorActuatorId ===
            row.sensorActuatorId)
      ) {
        this.barnControls.setControl(this.barnControls.controls.length, barnControl);
      } else {
        this.barnControls.removeAt(bcIndex);
        this.barnControls.setControl(bcIndex, barnControl);
      }
    }

    return foundBarn;
  }

  selectBarn(selection: MatSelectChange, sensorActuatorBarnRow: SensorActuatorBarnMapping) {
    const sensorActuatorBarnRows = this.tableConfig.dataSource.data.map(
      (p: SensorActuatorBarnMapping) => Object.assign({}, p),
    );

    const bcIndex = sensorActuatorBarnRows.findIndex(
      (sab) => sab.sensorActuatorId === sensorActuatorBarnRow.sensorActuatorId,
    );

    this.barnControls.controls[bcIndex].setValue(<SensorActuatorBarnMapping>selection.value);

    sensorActuatorBarnRow = {
      ...sensorActuatorBarnRow,
      barnId: this.barnControls.controls[bcIndex].value?.id as number,
    };
    this.updateDataSource();
  }

  updateDataSource() {
    const sensorActuatorBarnRows = this.tableConfig.dataSource.data.map(
      (p: SensorActuatorBarnMapping) => Object.assign({}, p),
    );

    sensorActuatorBarnRows.forEach((row, index) => {
      row.barnId = this.barnControls.controls[index]?.value?.id as number;
    });

    this.tableConfig.dataSource.data = sensorActuatorBarnRows;
  }

  save() {
    this.updateDataSource();
    const sensorActuatorBarnRows = this.tableConfig.dataSource.data.map(
      (s: SensorActuatorBarnMapping) => Object.assign({}, s),
    );

    this.store.dispatch(
      settingActions.upsertFarmResultSettingsRequest({
        sensorActuatorBarns: sensorActuatorBarnRows.map((s) => {
          const data = {
            id: s.id,
            barnId: s.barnId,
            deviceId: s.deviceId,
            farmSiteId: s.farmSiteId,
            sensorActuatorId: s.sensorActuatorId,
            sensorActuatorName: s.sensorActuatorName,
            farmResultLocationId: s.farmResultLocationId,
          } as SensorActuatorBarnMapping;
          return data;
        }),
      }),
    );
  }
}
